import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { DELIVERABLE_STATUSES } from "@demore/shared/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useWorkspace, useWorkspaceCan } from "./WorkspaceContext";

type Deliverable = {
  id: string;
  title: string;
  description: string | null;
  status: string;
  dueDate: string | null;
};

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : "—");

export function DeliverablesTab() {
  const { campaignId } = useWorkspace();
  // #2504-2506: manage controls only for super_admin / the owning campaign_owner.
  const { manageItems } = useWorkspaceCan();
  const qc = useQueryClient();
  const base = `/api/admin/campaigns/${campaignId}/deliverables`;
  const [createOpen, setCreateOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Deliverable | null>(null);

  const { data } = useQuery({
    queryKey: ["deliverables", campaignId],
    queryFn: () => api.get<{ items: Deliverable[] }>(base),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${base}/${id}`),
    onSuccess: () => {
      toast.success("Deliverable deleted");
      setPendingDelete(null);
      qc.invalidateQueries({ queryKey: ["deliverables", campaignId] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  // The status lifecycle (in_progress -> blocked/delivered/accepted) had no UI
  // control, so a deliverable could never be advanced and the acceptedAt stamp
  // (set server-side on "accepted") was unreachable. Inline Select fixes both.
  const setStatus = useMutation({
    mutationFn: (vars: { id: string; status: string }) =>
      api.patch(`${base}/${vars.id}`, { status: vars.status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deliverables", campaignId] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Update failed"),
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-head text-lg font-semibold">Deliverables</h2>
          <p className="text-xs text-muted-foreground">What this campaign owes its client, and where each stands.</p>
        </div>
        {manageItems && <Button onClick={() => setCreateOpen(true)}>New deliverable</Button>}
      </div>

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-admin-rule">
              <th className="p-3 font-medium">Title</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">Due</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} className="border-b border-admin-rule">
                <td className="p-3">
                  <div className="font-medium">{d.title}</div>
                  {d.description && (
                    <div className="text-xs text-muted-foreground line-clamp-1">{d.description}</div>
                  )}
                </td>
                <td className="p-3">
                  {manageItems ? (
                    <Select
                      value={d.status}
                      onChange={(e) => setStatus.mutate({ id: d.id, status: e.target.value })}
                      disabled={setStatus.isPending}
                      className="h-8 text-xs w-auto"
                      aria-label="Change deliverable status"
                    >
                      {DELIVERABLE_STATUSES.map((s) => (
                        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                      ))}
                    </Select>
                  ) : (
                    <Badge variant={d.status === "accepted" || d.status === "delivered" ? "ok" : "outline"}>
                      {d.status.replace(/_/g, " ")}
                    </Badge>
                  )}
                </td>
                <td className="p-3">{fmtDate(d.dueDate)}</td>
                <td className="p-3 text-right">
                  {manageItems && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => setPendingDelete(d)}
                      aria-label="Delete deliverable"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-xs text-muted-foreground">
                  No deliverables yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <CreateDeliverableDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        base={base}
        onSaved={() => qc.invalidateQueries({ queryKey: ["deliverables", campaignId] })}
      />

      <Dialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{pendingDelete?.title}"?</DialogTitle>
            <DialogDescription>This permanently removes the deliverable.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => pendingDelete && remove.mutate(pendingDelete.id)}
              disabled={remove.isPending}
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateDeliverableDialog({
  open,
  onOpenChange,
  base,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  base: string;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({ title: "", description: "", status: "not_started", dueDate: "" });
  const reset = () => setForm({ title: "", description: "", status: "not_started", dueDate: "" });

  const save = useMutation({
    mutationFn: () =>
      api.post(base, {
        title: form.title,
        description: form.description || undefined,
        status: form.status,
        dueDate: form.dueDate || undefined,
      }),
    onSuccess: () => {
      toast.success("Deliverable created");
      onSaved();
      reset();
      onOpenChange(false);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Failed"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New deliverable</DialogTitle>
          <DialogDescription>This deliverable belongs only to this campaign.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                  {DELIVERABLE_STATUSES.map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Due date</Label>
                <Input type="date" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!form.title || save.isPending}>
            {save.isPending ? "Saving…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
